import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import {
  DollarSign, TrendingUp, Briefcase, Percent, Users, Building2, Wrench, Download,
  CreditCard, ExternalLink, Receipt, FileText, Users2, Scale, Minus,
} from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import StatCard from "./StatCard";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";
import { downloadFinancialReportPdf } from "../../utils/financialPdf";
import { formatDateFilterRange } from "../../utils/dateUtils";

const GeneralReportTab = ({
  financialData,
  expensesSummary,
  techBalancesGeneral = [],
  formatCurrency,
  formatDate,
  exportToCSV,
  dateFilter,
  customStart,
  customEnd,
}) => {
  const { isDark } = useTheme();
  // Profit partners come from the backend (Settings → Business → Profit Split
  // Partners). Empty list = no split, full profit goes to the owner.
  const [profitPartners, setProfitPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let mounted = true;
    const token = localStorage.getItem("token");
    axios.get(`${API}/settings/profit-partners`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { if (mounted) setProfitPartners(res.data?.partners || []); })
      .catch(() => { if (mounted) setProfitPartners([]); })
      .finally(() => { if (mounted) setPartnersLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (!financialData) return null;

  const { summary, payments_by_type, jobs } = financialData;
  const profitColor = isDark ? "text-[#FFCC00]" : "text-amber-600";
  const profitBg = isDark ? "bg-[#FFCC00]/10" : "bg-amber-100";
  const linkColor = isDark ? "text-[#FFCC00] hover:text-[#FFCC00]/80" : "text-blue-600 hover:text-blue-800";
  const btnOutline = isDark ? "border-white/20 text-foreground hover:bg-theme-hover" : "border-gray-300 text-gray-700 hover:bg-gray-100";

  // Derived financials for new sections.
  //
  // IMPORTANT: `balanceAfterParts` must match what the exported PDF shows
  // in its TOTAL NET BALANCE row. The PDF builds it from per-source rows
  // where non-Direct sources have `source_commission` deducted from gross
  // revenue first (because that money goes to the lead-source partner,
  // not the business). We mirror that here by subtracting
  // `total_source_commission` from gross revenue. Without this, the
  // on-screen Net Profit Summary overstates profit by exactly the partner's
  // cut and disagrees with the PDF.
  const revenue = Number(summary?.total_revenue || 0);
  const parts = Number(summary?.total_parts_cost || 0);
  const sourceCommission = Number(summary?.total_source_commission || 0);
  const balanceAfterParts = revenue - parts - sourceCommission;
  // Mirror the PDF's "Total salaries (base only)" calculation so the General
  // tab and the exported PDF show the same number. Per-tech base salary =
  // net_payout − parts_reimbursement (post-fee compensation, excluding parts
  // reimbursement which was already deducted in the Balance section). The
  // previous on-page value used `summary.total_commission` (gross, also
  // included source commissions paid to lead-source providers) which made
  // the Net Profit deduction too large.
  const baseSalaries = (techBalancesGeneral || []).reduce(
    (s, tb) => s + Math.max(0, Number(tb.net_payout || 0) - Number(tb.parts_reimbursement || 0)),
    0,
  );
  const expensesTotal = Number(expensesSummary?.total || 0);
  const netProfitFinal = balanceAfterParts - baseSalaries - expensesTotal;
  // Compute each partner's share. If no partners configured, the full profit
  // goes to the owner (single implicit "Owner" share at 100%).
  const splitRows = profitPartners.length > 0
    ? profitPartners.map((p) => ({
        name: p.name,
        percentage: Number(p.percentage) || 0,
        amount: netProfitFinal * (Number(p.percentage) || 0) / 100,
      }))
    : [{ name: "Owner", percentage: 100, amount: netProfitFinal }];
  const netColor = netProfitFinal >= 0 ? (isDark ? "text-green-400" : "text-green-600") : (isDark ? "text-red-400" : "text-red-600");

  const fetchBusinessName = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/settings/business`, { headers: { Authorization: `Bearer ${token}` } });
      return res.data?.business_name || res.data?.name || "Your Business";
    } catch {
      return "Your Business";
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const businessName = await fetchBusinessName();
      const periodLabel = formatDateFilterRange(dateFilter, customStart, customEnd) || dateFilter || "All Time";

      // Build the same query string used by the rest of the Financials page
      // so totals match exactly across sections.
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const dqs = (() => {
        const p = new URLSearchParams({ date_filter: dateFilter });
        if (dateFilter === "custom") {
          if (customStart) p.append("start_date", customStart);
          if (customEnd) p.append("end_date", customEnd);
        }
        if (tz) p.append("tz", tz);
        return p.toString();
      })();

      // Fire all four extra fetches in parallel. Each is wrapped in a
      // try/catch so a single failure degrades the affected section to
      // "Data unavailable" rather than failing the whole export.
      const safeGet = async (path) => {
        try {
          const r = await axios.get(`${API}${path}?${dqs}`, { headers });
          return r.data;
        } catch (err) {
          console.warn(`[PDF] ${path} fetch failed:`, err?.message);
          return null;
        }
      };

      const [techData, sourceData, directData, expenseList] = await Promise.all([
        safeGet("/reports/technician"),
        safeGet("/reports/job-source"),
        safeGet("/reports/direct-jobs"),
        safeGet("/expenses"),
      ]);

      // ── Per-source rows ─────────────────────────────────────
      // Aggregate /reports/job-source jobs_list by source_name + add a
      // "Direct" row from /reports/direct-jobs.
      //
      // For non-Direct sources (Key & Key, Locksmith 911, etc.) the Balance
      // section's `Total` column reflects the company + tech share —
      // i.e. revenue MINUS the source partner's commission. This keeps the
      // Balance / Net Profit Summary honest: Net = Total − Parts equals the
      // money actually available to the business after the partner takes
      // their cut, so downstream salaries & expenses are deducted from the
      // correct base. Direct jobs have no source commission so their
      // revenue passes through unchanged.
      const sourceMap = new Map();
      for (const j of (sourceData?.jobs || [])) {
        const k = j.source_name || "Unknown source";
        if (!sourceMap.has(k)) sourceMap.set(k, { name: k, total: 0, parts: 0 });
        const row = sourceMap.get(k);
        const gross = Number(j.total || 0);
        const sourceCommission = Number(j.source_commission || 0);
        row.total += gross - sourceCommission;
        row.parts += Number(j.parts || 0);
      }
      if (directData?.summary) {
        const t = Number(directData.summary.total_revenue || 0);
        const p = Number(directData.summary.total_parts || 0);
        if (t > 0 || p > 0 || (directData.jobs || []).length > 0) {
          sourceMap.set("Direct", { name: "Direct", total: t, parts: p });
        }
      }
      const sourceRows = Array.from(sourceMap.values())
        .map((r) => ({ ...r, net: r.total - r.parts }))
        .sort((a, b) => b.total - a.total);

      // ── Per-tech salary rows ────────────────────────────────
      // Three columns map to three concepts:
      //   • Total Payout = `tb.net_payout` — matches the app's "Payout" column
      //     in Financials → Technician tab. This is the post-fee bottom line
      //     the tech actually receives.
      //   • Parts = `tb.parts_reimbursement` — informational, already inside
      //     net_payout (parts the tech bought and the company will reimburse).
      //   • Base Salary = Total Payout − Parts. The tech's pay net of fees
      //     and adjustments BUT excluding parts reimbursement, so the Net
      //     Profit Summary deduction (which uses Base Salary) does not
      //     re-deduct parts already removed in the Balance section.
      const techRows = (techData?.tech_balances || []).map((tb) => {
        const totalPayout = Number(tb.net_payout || 0);
        const parts = Number(tb.parts_reimbursement || 0);
        return {
          name: tb.name || "Unknown technician",
          baseSalary: totalPayout - parts,
          parts,
          totalPayout,
        };
      }).filter((r) => r.baseSalary > 0 || r.parts > 0 || r.totalPayout > 0);

      // ── Itemized expenses ───────────────────────────────────
      const expenses = (expenseList?.items || [])
        .slice()
        .sort((a, b) => String(a.date).localeCompare(String(b.date)));

      // ── Totals for the Net Profit Summary ────────────────
      // balanceAfterParts : sum of (revenue - parts) across all sources (same
      //                     figure as Balance section's TOTAL NET BALANCE —
      //                     parts are already accounted for here).
      // baseSalaries      : sum of BASE SALARY per tech (NOT salary+parts,
      //                     NOT summary.total_commission which includes
      //                     source commissions going to non-tech parties).
      // expenses          : sum of all expenses in the period.
      const totals = {
        balanceAfterParts: sourceRows.reduce((s, r) => s + Number(r.net || 0), 0),
        baseSalaries: techRows.reduce((s, r) => s + Number(r.baseSalary || 0), 0),
        expenses: Number(expensesSummary?.total || 0),
      };

      downloadFinancialReportPdf({
        businessName,
        periodLabel,
        sourceRows,
        techRows,
        expenses,
        totals,
        taxTotals: {
          charged: Number(summary?.total_tax_charged || 0),
          collected: Number(summary?.total_tax_collected || 0),
        },
        // Pass the configured partners directly. The PDF will render one row
        // per partner; if the array is empty it shows a single "Owner" row.
        profitPartners,
      });
      toast.success("Financial report downloaded");
    } catch (err) {
      console.error("PDF export failed:", err);
      toast.error("Failed to generate PDF");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Gold gradient PDF export button row — sits above every other section */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-foreground font-['Manrope']">General Report</h3>
          <p className="text-sm text-muted-foreground">Full revenue, payouts & profit breakdown</p>
        </div>
        <Button
          onClick={handleExportPdf}
          disabled={exporting}
          className="bg-gradient-to-r from-[#FFCC00] via-[#F5A623] to-[#FF9500] text-black font-semibold hover:opacity-90 shadow-[0_4px_20px_rgba(255,204,0,0.25)]"
          data-testid="export-financial-pdf-btn"
        >
          <FileText className="w-4 h-4 mr-2" />
          {exporting ? "Preparing…" : "Export PDF Report"}
        </Button>
      </div>

      {/* Responsive grid: tighter columns on tablets so the StatCard's
          `truncate` doesn't kick in on long labels (e.g. "Commission",
          "Net Profit"). 7 cards wrap as 4 + 3 across lg/xl widths and
          only line up in a single row at 2xl ≥ 1536 px. */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-7 gap-3">
        <StatCard title="Total Jobs" value={summary.total_jobs} icon={Briefcase} color="text-blue-500" bgColor="bg-blue-500/10" />
        <StatCard title="Total Revenue" value={formatCurrency(summary.total_revenue)} icon={DollarSign} color="text-green-600" bgColor="bg-green-500/10" />
        <StatCard title="Parts Cost" value={formatCurrency(summary.total_parts_cost)} icon={Wrench} color="text-orange-500" bgColor="bg-orange-500/10" />
        <StatCard title="Company Parts" value={formatCurrency(summary.company_parts)} icon={Building2} color="text-purple-500" bgColor="bg-purple-500/10" />
        <StatCard title="Tech Parts" value={formatCurrency(summary.tech_parts)} icon={Users} color="text-cyan-600" bgColor="bg-cyan-500/10" />
        <StatCard title="Commission" value={formatCurrency(summary.total_commission)} icon={Percent} color="text-red-500" bgColor="bg-red-500/10" />
        <StatCard title="Net Profit" value={formatCurrency(summary.net_profit)} icon={TrendingUp} color={profitColor} bgColor={profitBg} />
        {(summary.total_tax_charged > 0 || summary.total_tax_collected > 0) && (
          <>
            <StatCard
              title="Tax Charged"
              value={formatCurrency(summary.total_tax_charged)}
              icon={Receipt}
              color="text-teal-600"
              bgColor="bg-teal-500/10"
              data-testid="tax-charged-card"
              subtitle={summary.jobs_with_tax ? `${summary.jobs_with_tax} job${summary.jobs_with_tax === 1 ? "" : "s"}` : undefined}
            />
            <StatCard
              title="Tax Collected"
              value={formatCurrency(summary.total_tax_collected)}
              icon={Receipt}
              color="text-teal-600"
              bgColor="bg-teal-500/10"
              data-testid="tax-collected-card"
              subtitle="From payments received"
            />
          </>
        )}
      </div>

      {payments_by_type && payments_by_type.length > 0 && (
        <Card className="glass-card border-theme">
          <CardHeader className="pb-3">
            <CardTitle className="text-foreground text-base font-['Manrope'] flex items-center gap-2">
              <CreditCard className={`w-4 h-4 ${profitColor}`} />
              Payments by Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {payments_by_type.map((pt, i) => (
                <div key={i} className="bg-theme-surface rounded-lg px-4 py-2 border border-theme">
                  <p className="text-foreground font-medium">{pt.name}</p>
                  <p className={`${profitColor} font-semibold`}>{formatCurrency(pt.amount)}</p>
                  <p className="text-muted-foreground text-xs">{pt.count} transactions</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── NEW: Expenses Summary ─────────────────────────── */}
      <Card className="glass-card border-theme" data-testid="financials-expenses-summary">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-foreground text-base font-['Manrope'] flex items-center gap-2">
            <Receipt className={`w-4 h-4 ${profitColor}`} />
            Expenses Summary
          </CardTitle>
          <Link to="/dashboard/expenses" className={`text-xs ${linkColor} inline-flex items-center gap-1`} data-testid="financials-expenses-manage-link">
            Manage expenses <ExternalLink className="w-3 h-3" />
          </Link>
        </CardHeader>
        <CardContent>
          <div className="flex items-baseline justify-between mb-3">
            <p className="text-sm text-muted-foreground">Total for the selected period</p>
            <p className={`text-2xl font-bold ${profitColor} font-['Manrope']`} data-testid="financials-expenses-total">
              {formatCurrency(expensesTotal)}
            </p>
          </div>
          {expensesSummary?.by_category?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {expensesSummary.by_category.map((c) => (
                <div
                  key={c.category}
                  className="bg-theme-surface rounded-lg px-3 py-1.5 border border-theme flex items-center gap-2"
                  data-testid={`financials-expense-cat-${c.category}`}
                >
                  <span className="text-xs text-muted-foreground">{c.category}</span>
                  <span className="text-sm font-semibold text-foreground">{formatCurrency(c.amount)}</span>
                  <span className="text-[10px] text-muted-foreground">× {c.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No expenses recorded for this period.</p>
          )}
        </CardContent>
      </Card>

      {/* ── NEW: Net Profit Summary ───────────────────────── */}
      <Card className="glass-card border-theme" data-testid="financials-net-profit-summary">
        <CardHeader className="pb-3">
          <CardTitle className="text-foreground text-base font-['Manrope'] flex items-center gap-2">
            <Scale className={`w-4 h-4 ${profitColor}`} />
            Net Profit Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Balance after parts</span>
              <span className="text-foreground font-medium" data-testid="financials-balance-after-parts">{formatCurrency(balanceAfterParts)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground inline-flex items-center gap-1"><Minus className="w-3 h-3" /> Total salaries (base only)</span>
              <span className="text-red-500" data-testid="financials-total-salaries">−{formatCurrency(baseSalaries)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground inline-flex items-center gap-1"><Minus className="w-3 h-3" /> Total expenses</span>
              <span className="text-red-500" data-testid="financials-total-expenses">−{formatCurrency(expensesTotal)}</span>
            </div>
            <div className="h-px bg-white/10 dark:bg-white/10 my-2" />
            <div className="flex justify-between items-center">
              <span className="text-foreground font-semibold">Net Profit</span>
              <span className={`text-2xl font-bold ${netColor} font-['Manrope']`} data-testid="financials-net-profit-final">
                {formatCurrency(netProfitFinal)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── NEW: Profit Split ─────────────────────────────── */}
      <Card className="glass-card border-theme" data-testid="financials-profit-split">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-foreground text-base font-['Manrope'] flex items-center gap-2">
            <Users2 className={`w-4 h-4 ${profitColor}`} />
            Profit Split
          </CardTitle>
          <Link
            to="/dashboard/settings?tab=business"
            className={`text-xs ${linkColor} inline-flex items-center gap-1`}
            data-testid="financials-partners-edit"
          >
            Edit partners <ExternalLink className="w-3 h-3" />
          </Link>
        </CardHeader>
        <CardContent>
          {partnersLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : profitPartners.length === 0 ? (
            <div className="text-sm text-muted-foreground" data-testid="financials-no-partners">
              <p>No partners configured. The full net profit is allocated to the owner.</p>
              <p className="mt-1">
                <Link to="/dashboard/settings?tab=business" className={`${linkColor} hover:underline`}>
                  Add partners in Business Settings →
                </Link>
              </p>
              <div className="flex items-baseline justify-between mt-3 pt-3 border-t border-theme">
                <span className="text-foreground font-medium">Owner share</span>
                <span className={`text-2xl font-bold ${profitColor} font-['Manrope']`}>{formatCurrency(netProfitFinal)}</span>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {splitRows.map((row, i) => (
                <div key={i} className="flex items-baseline justify-between" data-testid={`financials-partner-row-${i}`}>
                  <div className="flex items-baseline gap-2">
                    <span className="text-foreground font-medium">{row.name}</span>
                    <span className="text-xs text-muted-foreground">{row.percentage}%</span>
                  </div>
                  <span className={`text-xl font-semibold ${profitColor} font-['Manrope']`} data-testid={`financials-partner-amount-${i}`}>
                    {formatCurrency(row.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="glass-card border-theme">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-foreground text-base font-['Manrope']">Job Details</CardTitle>
          <Button variant="outline" size="sm" onClick={() => exportToCSV(jobs, "financial_report")} className={btnOutline} data-testid="export-financial-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-theme hover:bg-transparent">
                  <TableHead className="text-muted-foreground">Job ID</TableHead>
                  <TableHead className="text-muted-foreground">Date</TableHead>
                  <TableHead className="text-muted-foreground">Customer</TableHead>
                  <TableHead className="text-muted-foreground">Service</TableHead>
                  <TableHead className="text-muted-foreground text-right">Charged</TableHead>
                  <TableHead className="text-muted-foreground text-right" title="Sales tax charged on the job — excluded from revenue and commissions">Tax</TableHead>
                  <TableHead className="text-muted-foreground text-right" title="Tax portion of payments actually received">Tax Coll.</TableHead>
                  <TableHead className="text-muted-foreground">Payments</TableHead>
                  <TableHead className="text-muted-foreground text-right">Parts</TableHead>
                  <TableHead className="text-muted-foreground text-right">Commission</TableHead>
                  <TableHead className="text-muted-foreground text-right">Net Profit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.length === 0 ? (
                  <TableRow className="border-theme">
                    <TableCell colSpan={11} className="text-center text-muted-foreground py-8">No jobs found for selected period</TableCell>
                  </TableRow>
                ) : (
                  jobs.map((job, i) => (
                    <TableRow key={job.job_id || i} className="border-theme hover:bg-theme-surface">
                      <TableCell>
                        <Link to={`/dashboard/jobs?job=${job.job_id}`} className={`${linkColor} flex items-center gap-1 font-mono text-xs`} data-testid={`job-link-${job.job_id}`}>
                          {job.job_id?.slice(-8) || "—"}<ExternalLink className="w-3 h-3" />
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{formatDate(job.completed_at)}</TableCell>
                      <TableCell className="text-foreground font-medium">{job.customer_name}</TableCell>
                      <TableCell className="text-muted-foreground">{job.service_name || job.title}</TableCell>
                      <TableCell className="text-green-600 text-right font-medium">{formatCurrency(job.total_charged)}</TableCell>
                      <TableCell className="text-teal-600 text-right text-sm" data-testid={`gen-tax-${job.job_id}`}>{(job.tax || 0) > 0 ? formatCurrency(job.tax) : "—"}</TableCell>
                      <TableCell className="text-teal-600 text-right text-sm" data-testid={`gen-tax-coll-${job.job_id}`}>{(job.tax_collected || 0) > 0 ? formatCurrency(job.tax_collected) : "—"}</TableCell>
                      <TableCell className="text-muted-foreground text-sm max-w-[150px] truncate">{job.payments}</TableCell>
                      <TableCell className="text-orange-500 text-right">{formatCurrency(job.parts_cost)}</TableCell>
                      <TableCell className="text-red-500 text-right">{formatCurrency(job.commission)}</TableCell>
                      <TableCell className={`${profitColor} text-right font-semibold`}>{formatCurrency(job.net_profit)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GeneralReportTab;
